import React, { useState, useEffect } from 'react';
import { formatBackendDateForDisplay, formatDateForBackend, getMinDate } from '../../services/dateUtils';
import { BOOKING_STATUSES } from '../../constants/statuses';
import Button from './Button';
import Modal from './Modal';

const DatePickerModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  booking, 
  loading,
  error 
}) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [errors, setErrors] = useState({});

  // Initialize dates when modal opens
  useEffect(() => {
    if (isOpen && booking) {
      setStartDate(formatBackendDateForDisplay(booking.start_date));
      setEndDate(formatBackendDateForDisplay(booking.end_date));
      setErrors({});
    }
  }, [isOpen, booking]);

  const validateForm = () => {
    const newErrors = {};
    const minDateString = getMinDate(0); // Allow editing from today
    
    // Check if this is an in-progress booking being edited by a host with allowHostEditInprogress enabled
    const isInProgressEdit = booking?.status === BOOKING_STATUSES.IN_PROGRESS && 
      window.wwoofBooking?.settings?.allowHostEditInprogress;
    
    if (!startDate) {
      newErrors.startDate = 'Start date is required';
    } else if (!isInProgressEdit) {
      // Only validate start date is not in the past for non-in-progress bookings
      const startDateBackend = formatDateForBackend(startDate);
      if (startDateBackend < minDateString) {
        newErrors.startDate = 'Start date cannot be in the past';
      }
    }

    if (!endDate) {
      newErrors.endDate = 'End date is required';
    } else if (endDate === startDate) {
      newErrors.endDate = 'End date must be after start date';
    } else {
      const startDateBackend = formatDateForBackend(startDate);
      const endDateBackend = formatDateForBackend(endDate);
      if (endDateBackend <= startDateBackend) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const startDateBackend = formatDateForBackend(startDate);
    const endDateBackend = formatDateForBackend(endDate);

    onSave({
      start_date: startDateBackend,
      end_date: endDateBackend,
    });
  };

  const handleClose = () => {
    setStartDate('');
    setEndDate('');
    setErrors({});
    onClose();
  };

  // Convert date input (YYYY-MM-DD) to DD-MM-YYYY for display
  const handleDateInputChange = (value, setterFunction) => {
    if (value) {
      const parts = value.split('-');
      if (parts.length === 3) {
        const [year, month, day] = parts;
        setterFunction(`${day}-${month}-${year}`);
      }
    } else {
      setterFunction('');
    }
  };

  // Convert DD-MM-YYYY to YYYY-MM-DD for input value
  const getInputValue = (ddmmyyyy) => {
    if (!ddmmyyyy) return '';
    const parts = ddmmyyyy.split('-');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return '';
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Edit Booking Dates">
      <div className="date-picker-form">
        {error && (
          <div className="error-banner">
            <strong>Error:</strong> {error}
          </div>
        )}
        
        <div className="form-group">
          <label htmlFor="start-date">
            Start Date:
            <input
              id="start-date"
              type="date"
              value={getInputValue(startDate)}
              onChange={(e) => handleDateInputChange(e.target.value, setStartDate)}
              className={errors.startDate ? 'error' : ''}
            />
          </label>
          {errors.startDate && (
            <div className="error-message">{errors.startDate}</div>
          )}
          {startDate && (
            <div className="date-preview">
              Display: {startDate}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="end-date">
            End Date:
            <input
              id="end-date"
              type="date"
              value={getInputValue(endDate)}
              onChange={(e) => handleDateInputChange(e.target.value, setEndDate)}
              className={errors.endDate ? 'error' : ''}
            />
          </label>
          {errors.endDate && (
            <div className="error-message">{errors.endDate}</div>
          )}
          {endDate && (
            <div className="date-preview">
              Display: {endDate}
            </div>
          )}
        </div>

        <div className="form-actions">
          <Button 
            variant="cancel" 
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            variant="edit" 
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DatePickerModal;
